import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import {
  adminRoles,
  type RoleCreate,
  type RoleRow,
} from "@/api/adminPanel";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  FormError,
  Input,
  Label,
} from "@/components/ui";

/**
 * Role Management — create/edit/delete org roles and bind permissions.
 *
 * System roles (org_id IS NULL, e.g. `super_admin`, `trader`, `org_admin`)
 * are seeded platform-wide and read-only here; org roles are fully mutable.
 * Backed by /api/v1/admin/roles + /api/v1/admin/permissions (adminRoles).
 */
export function AdminRolesPage() {
  const roles = useQuery({ queryKey: ["admin-roles"], queryFn: () => adminRoles.list() });
  const [editing, setEditing] = useState<RoleRow | null>(null);
  const [creating, setCreating] = useState(false);
  const qc = useQueryClient();
  const toast = useToast();

  const remove = useMutation({
    mutationFn: (id: string) => adminRoles.remove(id),
    onSuccess: () => {
      toast.push("Role deleted", "warn");
      qc.invalidateQueries({ queryKey: ["admin-roles"] });
    },
    onError: (e) =>
      toast.push(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e), "error"),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-base font-semibold text-term-fg">Roles &amp; permissions</h1>
          <p className="text-xs text-term-mute">
            Define what each role can do. Assign roles to users from the Users page.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>New role</Button>
      </div>

      <Card>
        <CardBody className="p-0">
          {roles.isLoading ? (
            <div className="p-3 text-sm text-term-mute">Loading…</div>
          ) : roles.data?.length === 0 ? (
            <EmptyState
              title="No roles yet"
              description="Create org roles (e.g. compliance officer, dealer, back office) and bind the permissions each needs."
              action={<Button onClick={() => setCreating(true)}>Add first role</Button>}
            />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Role</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Permissions</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Kind</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {roles.data?.map((r) => (
                  <tr key={r.id} className="border-t border-term-border align-top hover:bg-term-elev">
                    <td className="px-3 py-2">
                      <div className="font-medium text-term-fg">{r.name}</div>
                      {r.description && <div className="text-2xs text-term-dim">{r.description}</div>}
                    </td>
                    <td className="px-3 py-2">
                      {r.permission_keys.includes("super_admin") ? (
                        <span className="text-term-dim">All permissions</span>
                      ) : r.permission_keys.length === 0 ? (
                        <span className="text-term-mute">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {r.permission_keys.map((k) => (
                            <code
                              key={k}
                              className="rounded bg-term-elev px-1 py-0.5 text-2xs text-term-dim"
                            >
                              {k}
                            </code>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Badge tone={r.is_system ? "neutral" : "good"}>
                        {r.is_system ? "System" : "Org"}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {r.is_system ? (
                        <span className="text-2xs text-term-mute">read-only</span>
                      ) : (
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" onClick={() => setEditing(r)}>
                            Edit
                          </Button>
                          <Button
                            variant="danger"
                            onClick={() => {
                              if (confirm(`Delete role "${r.name}"? Users lose this role.`))
                                remove.mutate(r.id);
                            }}
                          >
                            Delete
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {creating && <RoleModal onClose={() => setCreating(false)} />}
      {editing && <RoleModal role={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

/** Create (no `role`) or edit (with `role`) a role and its permission bindings. */
function RoleModal({ role, onClose }: { role?: RoleRow; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const perms = useQuery({
    queryKey: ["admin-permissions"],
    queryFn: () => adminRoles.permissions(),
  });

  const [name, setName] = useState(role?.name ?? "");
  const [description, setDescription] = useState(role?.description ?? "");
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(role?.permission_keys ?? []),
  );
  const [err, setErr] = useState<string | null>(null);

  // Hide the meta `super_admin` permission — it's not assignable to org roles.
  const assignable = useMemo(
    () => (perms.data ?? []).filter((p) => p.key !== "super_admin"),
    [perms.data],
  );

  function toggle(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const save = useMutation({
    mutationFn: () => {
      const body: RoleCreate = {
        name,
        description: description || null,
        permission_keys: [...selected],
      };
      return role ? adminRoles.patch(role.id, body) : adminRoles.create(body);
    },
    onSuccess: () => {
      toast.push(role ? "Role updated" : "Role created", "success");
      qc.invalidateQueries({ queryKey: ["admin-roles"] });
      onClose();
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });

  return (
    <Modal open onClose={onClose} title={role ? `Edit role · ${role.name}` : "New role"}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          save.mutate();
        }}
        className="space-y-3"
      >
        <div>
          <Label>Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="compliance_officer" />
        </div>
        <div>
          <Label>Description</Label>
          <Input
            value={description ?? ""}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="optional"
          />
        </div>
        <div>
          <Label>Permissions</Label>
          {perms.isLoading ? (
            <div className="text-xs text-term-mute">Loading permissions…</div>
          ) : (
            <div className="max-h-64 space-y-1 overflow-y-auto rounded border border-term-border p-2">
              {assignable.map((p) => (
                <label
                  key={p.key}
                  className="flex cursor-pointer items-start gap-2 rounded px-1 py-1 hover:bg-term-elev"
                >
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={selected.has(p.key)}
                    onChange={() => toggle(p.key)}
                  />
                  <span className="text-xs">
                    <code className="text-term-fg">{p.key}</code>
                    {p.description && <span className="ml-1 text-term-dim">— {p.description}</span>}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>
        <FormError>{err}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={save.isPending}>
            {role ? "Save" : "Create"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
